/**
 * Car Selection Component for FlowRide Frontend
 * Provides car itinerary configuration interface with passenger count,
 * date selection, and itinerary calculation functionality.
 */

import { isPlatform } from "@ionic/react";
import { motion } from "motion/react";
import { type CSSProperties, type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useReducedMotion } from "../../hooks/useReducedMotion.hook";
import MyOwnCarIcon from "../../image/icons/itinerary/MyOwnCarIcon.svg?react";
import NavigationIcon from "../../image/icons/navigation/Navigation.svg";
import { isNavigationEnabled } from "../../services/FrontendConfigApi";
import { ItineraryService } from "../../services/ItineraryService";
import { ephemeralStore, navigationSessionSlice, useEphemeralSelector } from "../../store/ephemeral";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { type DateWithMode, type Route, type UserChoice } from "../../types/Forms";
import { type DepartureAndArrival } from "../../types/geocode-ui.types";
import { NavigationMode } from "../../types/Navigation";
import { isUiMotionEnabled } from "../shared/motion/uiMotion.tools";
import SectionLabel from "../shared/SectionLabel";
import CarItineraryButton from "./CarItineraryButton";

// import { DebugUtils } from '../../tools/DebugUtils';

interface ContainerProps {

    /** Source and destination information */
    "sourceDestination": DepartureAndArrival;

    /** Setter for selected itinerary ID */
    "setSelectedCarRouteId": Dispatch<SetStateAction<string>>;

    /** Currently selected itinerary ID */
    "selectedCarRouteId": string;

    /** Array of available car itineraries */
    "carItineraries": Route[];

    /** Setter for car itineraries */
    "setCarItineraries": Dispatch<SetStateAction<Route[]>>;

    /** Array of go itineraries */
    "goItineraries": Route[];

    /** Setter for go itineraries */
    "setGoItineraries": Dispatch<SetStateAction<Route[]>>;

    /** Array of backup return itineraries */
    "backupReturnItineraries": Route[];

    /** Setter for backup return itineraries */
    "setBackupReturnItineraries": Dispatch<SetStateAction<Route[]>>;

    /** Source date with mode information */
    "sourceDate": DateWithMode;

    /** Whether dark overlay palette tokens should be used. */
    "isDarkUi"?: boolean;
}

/** Itinerary service instance for API calls */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const itineraryService = new ItineraryService();

/**
 * Car Selection component for configuring car itinerary parameters.
 *
 * @param props - Component props.
 * @returns JSX element with car selection interface.
 */
const CarSelection: React.FC<ContainerProps> = ( {
    "sourceDestination": selectedInputs,
    selectedCarRouteId,
    setSelectedCarRouteId,
    carItineraries,
    setCarItineraries,
    goItineraries,
    setGoItineraries,
    backupReturnItineraries,
    setBackupReturnItineraries,
    isDarkUi = false,
} ) => {
    /** User choice state for itinerary configuration */
    const [ userChoice, setUserChoice ] = useState<UserChoice>( { "passengers": 1 } );
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const reducedMotion = useReducedMotion();
    const animationsEnabled = isUiMotionEnabled( reducedMotion );

    const navigationMode = useEphemeralSelector( navigationSessionSlice, ( session ) => session.navigationMode );
    const isFollowingNavigation = navigationMode === NavigationMode.CONSTRAINED_NAVIGATION;
    const setNavigationMode = useCallback( ( mode: NavigationMode ): void => {
        ephemeralStore.setValue( navigationSessionSlice, ( previous ) => ( {
            ...previous,
            "navigationMode": mode,
        } ) );
    }, [] );

    // Toggle navigation mode (replaces switchTrackingMode)
    const toggleNavigationMode = useCallback( () => {
        const isNavigating = navigationMode === NavigationMode.CONSTRAINED_NAVIGATION;

        if ( !isNavigating ) {
            setNavigationMode( NavigationMode.EXPLORATION );
        }
    }, [ navigationMode, setNavigationMode ] );

    /**
     * Dynamically update the user choice state.
     *
     * @param updates - Partial updates to apply to user choice.
     */
    const updateUserChoice = useCallback( ( updates: Partial<UserChoice> ) => {
        setUserChoice( ( current ) => {
            return {
                ...current,
                ...updates,
            };
        } );
    }, [] );

    useEffect( () => {
    // set default go itinerary at the beginning
        if ( goItineraries.length > 0 ) {
            const defaultItinerary = goItineraries[0].id;
            if ( selectedCarRouteId === "" ) {
                setSelectedCarRouteId( defaultItinerary );
                setTimeout( () => updateUserChoice( { "route": goItineraries[0] } ), 0 );
            }
        }

        // set default return itinerary at the beginning
        if ( backupReturnItineraries.length > 0 ) {
            if ( selectedCarRouteId === "" || userChoice.returnRoute === undefined ) {
                const defaultItinerary = backupReturnItineraries[0].id;
                if ( userChoice.isReturn ?? false ) {
                    setSelectedCarRouteId( defaultItinerary );
                }
                setTimeout( () => updateUserChoice( { "returnRoute": backupReturnItineraries[0] } ), 0 );
            }
        }
    }, [ userChoice, goItineraries, setSelectedCarRouteId, selectedCarRouteId, backupReturnItineraries, updateUserChoice ] );

    /**
     * Change map itineraries
     */
    useEffect( () => {
    // When changing return state
        if ( userChoice.isReturn ?? false ) {
            setCarItineraries( backupReturnItineraries );
        } else {
            setCarItineraries( goItineraries );
        }

        // When changing selected itinerary from return to go without disabling return state
        const existsInBackupReturnItineraries = backupReturnItineraries.some( ( item ) => item.id === selectedCarRouteId );
        const existsInGoItineraries = goItineraries.some( ( item ) => item.id === selectedCarRouteId );
        if ( existsInBackupReturnItineraries ) {
            setCarItineraries( backupReturnItineraries );
            setTimeout( () => updateUserChoice( { "returnRoute": backupReturnItineraries.find( ( item ) => item.id === selectedCarRouteId ) } ), 0 );
        }
        if ( existsInGoItineraries ) {
            setCarItineraries( goItineraries );
            setTimeout( () => updateUserChoice( { "route": goItineraries.find( ( item ) => item.id === selectedCarRouteId ) } ), 0 );
        }
    }, [
        backupReturnItineraries,
        goItineraries,
        selectedCarRouteId,
        setCarItineraries,
        setSelectedCarRouteId,
        updateUserChoice,
        userChoice.isReturn,
        userChoice.route,
    ] );

    /**
     * Clear back routes when switched off
     */
    useEffect( () => {
        if ( !( userChoice.isReturn ?? false ) ) {
            setBackupReturnItineraries( [] );
            setTimeout( () => updateUserChoice( { "returnRoute": undefined } ), 0 );
        }
    }, [ setBackupReturnItineraries, updateUserChoice, userChoice.isReturn ] );

    const navigate = useCallback( () => {
        if ( !isNavigationEnabled() ) {
            const { lat, lon } = selectedInputs.arrival;
            const isIOSPlatform = isPlatform( "ios" );

            let url: string;
            if ( isIOSPlatform ) {
                url = `comgooglemaps://?daddr=${ lat },${ lon }&directionsmode=driving`;
            } else {
                url = `https://www.google.com/maps/dir/?api=1&destination=${ lat },${ lon }`;
            }

            window.open( url, "_blank" );

            return;
        }


        if ( ( userChoice.isReturn ?? false ) && userChoice.returnRoute === undefined ) {
            alert( "Vous devez définir l'itinéraire de retour ou le désactiver !" );

            return;
        }
        if ( ( userChoice.isReturn ?? false ) && userChoice.returnDate === undefined ) {
            alert( "Vous devez définir la date de retour ou désactiver l'itinéraire de retour !" );

            return;
        }
        toggleNavigationMode();

    /*
     * const url = `https://www.google.com/maps/dir/?api=1&destination=${selectedInputs.arrival.lat},${selectedInputs.arrival.lon}`;
     * window.open(url, '_blank');
     * const response = await itineraryService.saveUserTrip(userChoice);
     * alert(response.data.message);
     */
    }, [ toggleNavigationMode, userChoice.isReturn, userChoice.returnDate, userChoice.returnRoute, selectedInputs.arrival ] );

    const navigateButtonStyle: CSSProperties = {
        "borderColor":     palette.brandPrimary,
        "backgroundColor": isFollowingNavigation ? palette.brandPrimary : `${ palette.brandPrimary }14`,
        "color":           isFollowingNavigation ? palette.textOnBrandPrimary : palette.brandPrimary,
    };

    return (
        <div
            className="flowrideScrollBar min-h-0 flex-1 overflow-y-auto p-2"
            id="TakeMyCar"
        >
            <div>
                <SectionLabel
                    className="mb-2"
                    icon={(
                        <MyOwnCarIcon
                            className="h-3.5 w-3.5"
                        />
                    )}
                    id="itinerary-car-selection-route-section"
                    isDarkUi={isDarkUi}
                    label="Quelle route ?"
                />
                {/* Loop on routes */}
                <div className="overflow-x-auto py-1">
                    <div className="flex items-center justify-center whitespace-nowrap">
                        <CarItineraryButton
                            isDarkUi={isDarkUi}
                            itineraries={carItineraries}
                            selectedCarRouteId={selectedCarRouteId}
                            selectedInputs={selectedInputs}
                            setBackupReturnItineraries={setBackupReturnItineraries}
                            setGoItineraries={setGoItineraries}
                            setItineraries={setCarItineraries}
                            setSelectedCarRouteId={setSelectedCarRouteId}
                            type="no_autoroute_peages"
                            updateUserChoice={updateUserChoice}
                            userChoice={userChoice}
                        />
                        <CarItineraryButton
                            isDarkUi={isDarkUi}
                            itineraries={carItineraries}
                            selectedCarRouteId={selectedCarRouteId}
                            selectedInputs={selectedInputs}
                            setBackupReturnItineraries={setBackupReturnItineraries}
                            setGoItineraries={setGoItineraries}
                            setItineraries={setCarItineraries}
                            setSelectedCarRouteId={setSelectedCarRouteId}
                            type="autoroute_peages"
                            updateUserChoice={updateUserChoice}
                            userChoice={userChoice}
                        />
                        <CarItineraryButton
                            isDarkUi={isDarkUi}
                            itineraries={carItineraries}
                            selectedCarRouteId={selectedCarRouteId}
                            selectedInputs={selectedInputs}
                            setBackupReturnItineraries={setBackupReturnItineraries}
                            setGoItineraries={setGoItineraries}
                            setItineraries={setCarItineraries}
                            setSelectedCarRouteId={setSelectedCarRouteId}
                            type="eco_friendly"
                            updateUserChoice={updateUserChoice}
                            userChoice={userChoice}
                        />
                    </div>
                </div>
            </div>
            <div
                className="mt-3 flex w-full justify-center px-1 pb-1"
                id="itinerary-car-selection-navigate-cta"
            >
                <motion.button
                    aria-label={isFollowingNavigation ? "Arrêter la navigation" : "Commencer la navigation"}
                    className="inline-flex items-center gap-2 rounded-full border-2 border-solid px-4 py-2 text-xs font-bold shadow-sm transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                    onClick={() => {
                        navigate();
                    }}
                    style={navigateButtonStyle}
                    type="button"
                    whileTap={animationsEnabled ? { "scale": 0.94 } : undefined}
                >
                    <img
                        alt=""
                        aria-hidden={true}
                        className="h-4 w-4 shrink-0"
                        src={NavigationIcon}
                    />
                    {isFollowingNavigation ? "Arrêter la navigation" : "Commencer la navigation"}
                </motion.button>
            </div>
        </div>
    );
};

export default CarSelection;
